/**
 * IncentiveBuilder — smart, guided creator financial-incentive picker.
 *
 * Replaces the old trio (Commission % dropdown + Bonus Type dropdown + Bonus
 * Details text input) with a modern preset + custom flow that matches how
 * brand managers actually think about rewards:
 *
 *   1. Base commission — chip row of common values (8 / 10 / 12 / 15%) + custom.
 *   2. Performance boost — 4 opinionated preset "tracks" (none / milestone /
 *      tiered / flat-fee) that each expand into the minimum fields required.
 *   3. Live "Creator offer preview" card that renders exactly what creators
 *      will see in their RFP — so brand managers aren't guessing.
 */
import React, { useMemo } from 'react';
import { DollarSign, Zap, Layers, Gift, Trophy, Sparkles, Info } from 'lucide-react';
import { T, P, BG, CARD, BORDER } from './constants';

const COMMISSION_PRESETS = ['8%', '10%', '12%', '15%'];

const BOOST_TRACKS = [
  {
    id: 'none',
    label: 'Commission only',
    desc: 'Pure revenue share. Simplest, most common.',
    icon: DollarSign,
    fields: [],
  },
  {
    id: 'milestone',
    label: 'Unit milestone bonus',
    desc: 'Flat bonus when the creator crosses a unit threshold.',
    icon: Trophy,
    fields: [
      { id: 'bonus_amount', label: 'Bonus amount ($)', type: 'number', placeholder: '500', prefix: '$' },
      { id: 'unit_threshold', label: 'Units sold to unlock', type: 'number', placeholder: '1000' },
    ],
    buildSummary: (v) => v.bonus_amount && v.unit_threshold
      ? `+ $${Number(v.bonus_amount).toLocaleString()} bonus at ${Number(v.unit_threshold).toLocaleString()} units sold`
      : 'Fill in bonus amount + unit threshold',
  },
  {
    id: 'tiered',
    label: 'Tiered volume bonus',
    desc: 'Higher commission rate kicks in at higher volume — motivates top performers.',
    icon: Layers,
    fields: [
      { id: 'tier_1_units', label: 'Tier 1 unit threshold', type: 'number', placeholder: '500' },
      { id: 'tier_1_bonus_pct', label: 'Tier 1 bonus (%)', type: 'number', placeholder: '3', suffix: '%' },
      { id: 'tier_2_units', label: 'Tier 2 unit threshold', type: 'number', placeholder: '2000' },
      { id: 'tier_2_bonus_pct', label: 'Tier 2 bonus (%)', type: 'number', placeholder: '5', suffix: '%' },
    ],
    buildSummary: (v) => v.tier_1_units && v.tier_1_bonus_pct
      ? `+${v.tier_1_bonus_pct}% over ${Number(v.tier_1_units).toLocaleString()} units` +
        (v.tier_2_units && v.tier_2_bonus_pct ? `, +${v.tier_2_bonus_pct}% over ${Number(v.tier_2_units).toLocaleString()}` : '')
      : 'Fill in at least the tier-1 threshold + %',
  },
  {
    id: 'flat',
    label: 'Fixed performance fee',
    desc: 'Flat payout regardless of sales. Good for brand/awareness waves.',
    icon: Gift,
    fields: [
      { id: 'flat_fee', label: 'Fixed fee ($)', type: 'number', placeholder: '1500', prefix: '$' },
    ],
    buildSummary: (v) => v.flat_fee
      ? `Flat $${Number(v.flat_fee).toLocaleString()} fee per creator`
      : 'Enter the flat fee amount',
  },
];

export default function IncentiveBuilder({ incentive, onChange }) {
  const {
    commission_pct = '10%',
    custom_commission = '',
    boost_track = 'none',
    boost_fields = {},
  } = incentive || {};

  const selectedTrack = BOOST_TRACKS.find(t => t.id === boost_track) || BOOST_TRACKS[0];
  const commissionLabel = commission_pct === 'custom' ? `${custom_commission || '—'}%` : commission_pct;

  const summary = useMemo(() => {
    const pieces = [`${commissionLabel} commission`];
    if (selectedTrack.id !== 'none' && selectedTrack.buildSummary) {
      pieces.push(selectedTrack.buildSummary(boost_fields));
    }
    return pieces;
  }, [commissionLabel, selectedTrack, boost_fields]);

  const updateField = (fieldId, value) => {
    onChange({ ...incentive, boost_fields: { ...boost_fields, [fieldId]: value } });
  };

  return (
    <div className="rounded-xl p-5 space-y-5" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="incentive-builder">
      {/* ═══ Header ═══ */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-xs font-bold text-white flex items-center gap-2">
            <Zap className="h-3.5 w-3.5" style={{ color: T }} />Creator Incentives
          </h3>
          <p className="text-[9px] text-gray-600 mt-0.5">How creators get paid. Transparent, in one glance.</p>
        </div>
      </div>

      {/* ═══ Step 1: Base commission ═══ */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-5 h-5 rounded-full bg-[#00F5FF]/15 flex items-center justify-center text-[9px] font-black text-[#00F5FF]">1</span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Base commission</span>
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="commission-chips">
          {COMMISSION_PRESETS.map(pct => {
            const active = commission_pct === pct;
            return (
              <button key={pct} type="button" onClick={() => onChange({ ...incentive, commission_pct: pct })}
                      data-testid={`commission-chip-${pct}`}
                      className="px-4 py-2 rounded-lg text-xs font-bold transition-all"
                      style={{
                        background: active ? T : BG,
                        color: active ? '#000' : '#888',
                        border: `1px solid ${active ? T : BORDER}`,
                        boxShadow: active ? `0 0 14px ${T}40` : 'none',
                      }}>{pct}</button>
            );
          })}
          <button type="button" onClick={() => onChange({ ...incentive, commission_pct: 'custom' })}
                  data-testid="commission-chip-custom"
                  className="px-4 py-2 rounded-lg text-xs font-bold transition-all"
                  style={{
                    background: commission_pct === 'custom' ? T : BG,
                    color: commission_pct === 'custom' ? '#000' : '#888',
                    border: `1px solid ${commission_pct === 'custom' ? T : BORDER}`,
                  }}>Custom</button>
          {commission_pct === 'custom' && (
            <div className="relative">
              <input type="number" min="0" max="100" value={custom_commission}
                     onChange={e => onChange({ ...incentive, custom_commission: e.target.value })}
                     placeholder="e.g., 20"
                     data-testid="commission-custom-input"
                     className="w-28 pl-3 pr-7 py-2 rounded-lg text-xs text-white focus:outline-none"
                     style={{ background: BG, border: `1px solid ${T}40` }} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 font-bold">%</span>
            </div>
          )}
        </div>
        <p className="text-[9px] text-gray-600 mt-2 flex items-center gap-1">
          <Info className="h-2.5 w-2.5" />Applies to ShopMy + TikTok Shop revenue. Paid monthly via Stripe Connect.
        </p>
      </div>

      {/* ═══ Step 2: Boost track ═══ */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-5 h-5 rounded-full bg-[#FF6BBD]/15 flex items-center justify-center text-[9px] font-black text-[#FF6BBD]">2</span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Performance boost <span className="text-gray-600 font-normal normal-case">(optional)</span></span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="boost-tracks">
          {BOOST_TRACKS.map(track => {
            const active = boost_track === track.id;
            const Icon = track.icon;
            return (
              <button key={track.id} type="button"
                      onClick={() => onChange({ ...incentive, boost_track: track.id, boost_fields: {} })}
                      data-testid={`boost-track-${track.id}`}
                      className="text-left p-3 rounded-lg transition-all"
                      style={{
                        background: active ? `${P}10` : BG,
                        border: `1px solid ${active ? `${P}50` : BORDER}`,
                        boxShadow: active ? `0 0 18px ${P}30` : 'none',
                      }}>
                <Icon className="h-4 w-4 mb-1.5" style={{ color: active ? P : '#555' }} />
                <p className="text-[10px] font-bold text-white leading-tight">{track.label}</p>
                <p className="text-[8px] text-gray-600 mt-1 leading-snug">{track.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Step 2b: Track fields (conditional) ═══ */}
      {selectedTrack.fields.length > 0 && (
        <div className="rounded-lg p-3 space-y-2.5"
             style={{ background: BG, border: `1px solid ${P}25` }}
             data-testid="boost-track-fields">
          <p className="text-[10px] font-bold text-white flex items-center gap-1.5">
            <selectedTrack.icon className="h-3 w-3" style={{ color: P }} />{selectedTrack.label} — details
          </p>
          <div className="grid grid-cols-2 gap-2">
            {selectedTrack.fields.map(f => (
              <div key={f.id}>
                <label className="text-[9px] text-gray-600 uppercase tracking-wider font-semibold block mb-1">{f.label}</label>
                <div className="relative">
                  {f.prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-600 font-bold">{f.prefix}</span>}
                  <input type={f.type} value={boost_fields[f.id] || ''}
                         onChange={e => updateField(f.id, e.target.value)}
                         placeholder={f.placeholder}
                         data-testid={`boost-field-${f.id}`}
                         className={`w-full ${f.prefix ? 'pl-6' : 'pl-3'} ${f.suffix ? 'pr-6' : 'pr-3'} py-2 rounded-lg text-xs text-white placeholder-gray-700 focus:outline-none`}
                         style={{ background: CARD, border: `1px solid ${BORDER}` }} />
                  {f.suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-600 font-bold">{f.suffix}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ Step 3: Creator offer preview ═══ */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="w-5 h-5 rounded-full bg-green-500/15 flex items-center justify-center text-[9px] font-black text-green-400">3</span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">What creators will see</span>
        </div>
        <div className="rounded-lg p-4 relative overflow-hidden"
             style={{
               background: 'linear-gradient(135deg, rgba(0,245,255,0.05), rgba(255,107,189,0.03), #0A0A0A)',
               border: `1px solid ${T}30`,
             }}
             data-testid="incentive-preview">
          <div className="absolute -top-10 -right-8 w-32 h-32 rounded-full blur-3xl opacity-20" style={{ background: T }} />
          <div className="relative flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: `${T}18` }}>
              <Sparkles className="h-4 w-4" style={{ color: T }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Creator offer</p>
              {summary.map((line, i) => (
                <p key={`offer-line-${line}`} className="text-sm font-bold text-white leading-snug">
                  {i === 0 && <span className="text-[#00F5FF]">{line.split(' ')[0]} </span>}
                  {i === 0 ? line.split(' ').slice(1).join(' ') : line}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
